import * as React from "react"
import { cn } from "../../utils"

const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement>
>(({ className, ...props }, ref) => (
  <div className="relative w-full overflow-auto">
    <table
      ref={ ref }
      className={ cn("w-full caption-bottom text-sm", className) }
      { ...props }
    />
  </div>
))
Table.displayName = "Table"

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ ref } className={ cn("[&_tr]:border-muted", className) } { ...props } />
))
TableHeader.displayName = "TableHeader"

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ ref }
    className={ cn("[&_tr:last-child]:border-0", className) }
    { ...props }
  />
))
TableBody.displayName = "TableBody"

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ ref }
    className={ cn("bg-muted font-medium text-foreground", className) }
    { ...props }
  />
))
TableFooter.displayName = "TableFooter"

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ ref }
    className={ cn(
      // A row wants a rule UNDER it, not a box around it. `border` drew all
      // four sides, so every row also painted a left and a right edge — which
      // stacked against the table wrapper's own frame and read as a doubled,
      // bolder line down both sides of the table.
      "border-b border-muted transition-colors data-[state=selected]:bg-muted",
      className
    ) }
    { ...props }
  />
))
TableRow.displayName = "TableRow"

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ ref }
    className={ cn(
      "h-10 px-4 text-left align-middle text-sm text-muted-foreground",
      className
    ) }
    { ...props }
  />
))
TableHead.displayName = "TableHead"

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ ref }
    className={ cn("p-4 align-middle", className) }
    { ...props }
  />
))
TableCell.displayName = "TableCell"

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ ref }
    className={ cn("mt-4 text-sm text-muted-foreground", className) }
    { ...props }
  />
))
TableCaption.displayName = "TableCaption"

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
}
